import type { ContentIcon } from '~/models/Content/ContentIcon'
import { useRouter } from 'vue-router'
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { faAlignLeft, faBookOpen, faBooks, faBowlChopsticksNoodles, faBrain, faComments, faEyes, faHeart, faHighlighter, faLightbulbOn, faListUl, faMindShare, faPenAlt, faPencil, faPencilRuler, faQuestion, faSearch, faUniversalAccess, faVideo } from '@fortawesome/duotone-regular-svg-icons'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import useContentApi from '~/api/contentApi'

export function useIcons() {
  const duotone = new Map([
    ['comments', faComments],
    ['pencil-ruler', faPencilRuler],
    ['pencil', faPencil],
    ['video', faVideo],
    ['universal-access', faUniversalAccess],
    ['book-open', faBookOpen],
    ['list-ul', faListUl],
    ['align-left', faAlignLeft],
    ['search', faSearch],
    ['question', faQuestion],
    ['mind-share', faMindShare],
    ['brain', faBrain],
    ['books', faBooks],
    ['pen-alt', faPenAlt],
    ['highlighter', faHighlighter],
    ['lightbulb-on', faLightbulbOn],
    ['bowl-chopsticks-noodles', faBowlChopsticksNoodles],
    ['eyes', faEyes],
    ['heart', faHeart],
  ])

  const identifiers = new Set([...duotone.values()])

  const { findContents } = useContentApi()
  const router = useRouter()
  const icons = useQuery({
    queryKey: ['icon-taxonomy', ...identifiers],
    initialData: [],
    initialDataUpdatedAt: 0,
    staleTime: 1000 * 60 * 60 * 12,
    queryFn() {
      return findContents<ContentIcon>({
        contentTypeCriterion: [ContentType.IconTag],
        mainLocationCriterion: true,
        subtreeCriterion: [Subtree.Taxonomy],
        parentLocationIdCriterion: [import.meta.env.VITE_ICON_TAXONOMY_LOCATION_ID],
        sortField: 'published',
        sortOrder: 'asc',
      }, identifiers.size)
    },
  })

  function iconByContentId(contentId: number, iconset = duotone) {
    return computed(() => {
      const identifier = icons.data.value.find(icon => icon.contentId === contentId)?.identifier
      return identifier ? iconset.get(identifier) : undefined
    })
  }

  return {
    duotone,
    iconByContentId,
  }
}
